import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { userDataService } from '../services';
import { createHeaders, getApiUrls, MissingAuthTokenError } from '../services/serviceConfig';

import type { GenerateCustomSessionPayload, GeneratedSession, PracticeCourse, PracticeLessonSelection } from '../types/practiceCourses';

export type CustomPracticeStep = 'subject' | 'lessons' | 'generating' | null;

type UseCustomPracticeFlowResult = {
  closeFlow: () => void;
  handleLessonsSelect: (selection: PracticeLessonSelection) => void;
  handleSubjectSelect: (course: PracticeCourse) => void;
  hasGenerationError: boolean;
  isLoadingPracticeCourses: boolean;
  openFlow: () => void;
  practiceCourses: PracticeCourse[];
  selectedCourseId: string | null;
  selectedCourseTitle: string;
  step: CustomPracticeStep;
};

/**
 * Orchestrates the "Elegir qué practicar" dialog flow (TANAI-1869):
 * subject selection → lesson selection → session generation → navigation.
 *
 * Popup 2 (PracticeLessonSelectionDialog) fetches its own ToC WITH progress and
 * emits `{ lessonIds, unitIds }` directly, so this hook no longer touches the
 * data-manager ToC: it only tracks the selected subject and runs generation.
 */
function useCustomPracticeFlow(): UseCustomPracticeFlowResult {
  const history = useHistory();

  const [step, setStep] = useState<CustomPracticeStep>(null);
  const [practiceCourses, setPracticeCourses] = useState<PracticeCourse[]>([]);
  const [isLoadingPracticeCourses, setIsLoadingPracticeCourses] = useState<boolean>(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedCourseTitle, setSelectedCourseTitle] = useState<string>('');

  const [hasGenerationError, setHasGenerationError] = useState<boolean>(false);

  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;
    return function () {
      isMountedRef.current = false;
    };
  }, []);

  const fetchPracticeCourses = useCallback(async () => {
    setIsLoadingPracticeCourses(true);
    try {
      const data = (await userDataService.getCoursesPractice()) as PracticeCourse[];
      if (isMountedRef.current) {
        setPracticeCourses(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setPracticeCourses([]);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoadingPracticeCourses(false);
      }
    }
  }, []);

  const resetState = useCallback(() => {
    setSelectedCourseId(null);
    setSelectedCourseTitle('');
    setHasGenerationError(false);
  }, []);

  const openFlow = useCallback(() => {
    resetState();
    setStep('subject');
    fetchPracticeCourses();
  }, [fetchPracticeCourses, resetState]);

  const closeFlow = useCallback(() => {
    setStep(null);
    resetState();
  }, [resetState]);

  const handleSubjectSelect = useCallback((course: PracticeCourse) => {
    setSelectedCourseId(course.id);
    setSelectedCourseTitle(course.title);
    setStep('lessons');
  }, []);

  const navigateToFirstActivity = useCallback(
    (session: GeneratedSession | null) => {
      const firstActivity = session?.activityInstances?.[0];
      if (session?.id && firstActivity?.id) {
        history.push(`/activities/sessions/${session.id}/activities/${firstActivity.id}`);
      }
    },
    [history]
  );

  const generateCustomSession = useCallback(
    async (lessonIds: string[], unitIds: string[]) => {
      if (!selectedCourseId) return;

      setHasGenerationError(false);
      setStep('generating');

      try {
        const urls = getApiUrls();
        const payload: GenerateCustomSessionPayload = { courseId: selectedCourseId, lessonIds, unitIds };
        const response = await fetch(urls.activities.generateCustomSession(), {
          method: 'POST',
          headers: createHeaders(),
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`Failed to generate custom session: HTTP ${response.status}`);
        }

        const session = (await response.json()) as GeneratedSession;

        if (!isMountedRef.current) return;

        navigateToFirstActivity(session);
        closeFlow();
      } catch (error) {
        if (!(error instanceof MissingAuthTokenError) && isMountedRef.current) {
          setHasGenerationError(true);
          setStep('subject');
        }
      }
    },
    [closeFlow, navigateToFirstActivity, selectedCourseId]
  );

  const handleLessonsSelect = useCallback(
    (selection: PracticeLessonSelection) => {
      generateCustomSession(selection.lessonIds, selection.unitIds);
    },
    [generateCustomSession]
  );

  return {
    closeFlow,
    handleLessonsSelect,
    handleSubjectSelect,
    hasGenerationError,
    isLoadingPracticeCourses,
    openFlow,
    practiceCourses,
    selectedCourseId,
    selectedCourseTitle,
    step,
  };
}

export default useCustomPracticeFlow;
